import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  UserPlus, Zap, Layers, Mail, Monitor, ListChecks, Bell, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const EmployeeOnboardingPage = () => {
  const benefits = [
    {
      icon: Layers,
      title: 'Role-based provisioning templates for every hire type',
      description:
        'Pre-built access bundles by role, department, and location ensure consistent provisioning for every new hire, contractor, or temp worker - no manual ticket per system.',
    },
    {
      icon: Zap,
      title: 'Multi-system orchestration from HRIS to endpoint',
      description:
        'A single HRIS event triggers end-to-end provisioning across IAM, email, M365, core apps, ITSM, and hardware - fully automated and sequenced.',
    },
    {
      icon: ListChecks,
      title: 'Audit logging and governance controls throughout',
      description:
        'Every provisioning step, approval, and completion event is logged with actor, timestamp, and outcome - audit-ready from day one.',
    },
  ];

  const capabilities = [
    'Integrate HRIS (Workday/SAP) + Azure AD + M365/Exchange + ITSM + CMDB/Asset + App provisioning',
    'Define role templates: access bundles by role, department, and location',
    'Configure approvals: manager, security, and budget workflows',
    'Configure exceptions: non-standard roles, special access, and contractors',
    'Notifications + dashboards: readiness and completion tracking',
  ];

  const features = [
    {
      icon: Zap,
      title: 'HRIS-Triggered Workflows',
      desc: 'New hire records in Workday or SAP trigger the full onboarding sequence automatically - no manual kickoff required from IT or HR.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: Layers,
      title: 'Role-Based Access Templates',
      desc: 'Pre-configured access bundles by role, department, and location ensure every hire receives the right access from day one - consistently.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: Mail,
      title: 'M365 & App Provisioning',
      desc: 'Mailbox creation, M365 license assignment, group memberships, and SaaS app access are provisioned automatically based on the role template.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: Monitor,
      title: 'Hardware & Badge Requests',
      desc: 'ITSM tasks are auto-created for hardware allocation, badge provisioning, and desk assignment - routed to the correct fulfillment teams.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: ListChecks,
      title: 'ITSM Task Automation',
      desc: 'All onboarding tasks - standard and exception - are tracked in ITSM with SLA monitoring, owner assignment, and completion verification.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: Bell,
      title: 'Manager Notifications',
      desc: 'The hiring manager and new hire receive automated status updates - when accounts are ready, hardware is en route, and onboarding is complete.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'HR event triggers onboarding workflow',
      desc: 'A new hire record published in Workday or SAP fires the automation trigger, initiating the onboarding sequence without any manual IT or HR intervention.',
    },
    {
      step: '02',
      title: 'Automation validates role, manager, start date, location',
      desc: 'The workflow validates all required fields from the HRIS record - role, cost center, manager, office location - before selecting the provisioning template.',
    },
    {
      step: '03',
      title: 'Provisions accounts, mailbox, groups, licenses, core apps',
      desc: 'Azure AD account, Exchange mailbox, M365 licenses, security group memberships, and core SaaS app access are provisioned automatically based on the role template.',
    },
    {
      step: '04',
      title: 'Opens tasks for hardware, badges, and specialized access',
      desc: 'ITSM tasks are created and routed to the hardware team, facilities for badge issuance, and security/budget approvers for any non-standard access requests.',
    },
    {
      step: '05',
      title: 'Updates ITSM and notifies manager and new hire',
      desc: 'The onboarding ITSM record is updated at each milestone. The manager receives a readiness summary and the new hire receives login and first-day instructions.',
    },
    {
      step: '06',
      title: 'Closes once all tasks complete',
      desc: 'The onboarding ticket auto-closes once all provisioning and fulfillment tasks are verified complete - with a final summary retained for audit.',
    },
  ];

  const stats = [
    { value: '~40%', label: 'L1 workload reduction' },
    { value: 'Faster', label: 'Time-to-productivity for new hires' },
    { value: '~45%', label: 'Automation potential for IT onboarding' },
    { value: 'Improved', label: 'SLA compliance and faster MTTR' },
  ];

  const faqs = [
    {
      q: 'Can onboarding be customized per business unit?',
      a: 'Yes. Role templates are configurable at any level of granularity - global defaults with overrides by business unit, department, location, or employment type. Each template defines the exact access bundle, approval requirements, and fulfillment tasks for that segment.',
    },
    {
      q: 'What if the HR data is incomplete or has errors?',
      a: 'The automation includes validation rules that check required fields before triggering provisioning. If critical data is missing (e.g., manager, role, cost center), the workflow pauses and notifies the HR coordinator to correct the record - preventing incomplete onboarding.',
    },
    {
      q: 'Does it handle contractors and temporary workers?',
      a: 'Yes. Separate templates can be defined for contractors, temps, and fixed-term employees - with different access scopes, shorter license durations, and specific offboarding triggers aligned to contract end dates.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCaseEmployeeOnboarding" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Employee Onboarding","item":"https://digian.ai/use-cases/employee-onboarding"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">IT Helpdesk / Employee Onboarding</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Automated Employee Onboarding - Identity, Email, Apps, Hardware
            </h1>
            <p className="text-lg text-slate-600">
              HR-triggered onboarding workflows that provision users end-to-end across IAM, email, apps, endpoint and hardware, plus ticket and task automation across IT teams.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['HRIS-triggered automation', 'Role-based access templates', 'End-to-end audit logging'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">Onboarding Automation</p>
                <p className="text-sm text-slate-600">HRIS to endpoint provisioning - fully automated</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <UserPlus className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why IT teams automate onboarding</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed to automate end-to-end onboarding</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How automated onboarding works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <UserPlus className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">Every new hire ready on day one - automatically</h2>
            <p className="text-xl text-indigo-100">
              No manual tickets per system. No missed access. No delayed hardware. Just a fully provisioned employee, on schedule.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">HRIS-triggered · Role-based templates · Full audit trail</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EmployeeOnboardingPage;
